/**
 * service_line is a native Postgres enum extended at runtime via the
 * add_service_line/seed_default_milestones RPCs (migration 00108) — this
 * hook is the single source of truth for the frontend, replacing the old
 * hardcoded SERVICE_LINE_LABELS map.
 */
import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../../../lib/supabase'

export interface ServiceLineRow {
  key: string
  label: string
  is_active: boolean
  sort_order: number | null
  created_at: string
}

export function useServiceLines() {
  const [serviceLines, setServiceLines] = useState<ServiceLineRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mutating, setMutating] = useState(false)

  const refetch = useCallback(async () => {
    setLoading(true)
    const { data, error: fetchError } = await supabase
      .from('service_lines')
      .select('*')
      .order('sort_order', { ascending: true, nullsFirst: false })
      .order('label', { ascending: true })

    if (fetchError) {
      setError(fetchError.message)
    } else {
      setServiceLines((data ?? []) as ServiceLineRow[])
      setError(null)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    refetch()
  }, [refetch])

  const activeServiceLines = useMemo(() => serviceLines.filter((s) => s.is_active), [serviceLines])

  const labels = useMemo(() => {
    const map: Record<string, string> = {}
    for (const s of serviceLines) map[s.key] = s.label
    return map
  }, [serviceLines])

  function getLabel(key: string): string {
    return labels[key] ?? key
  }

  /** Adds a new service line: extends the enum + label row, then seeds its default 8-stage milestone workflow. Two sequential RPC calls — a value added by ALTER TYPE ADD VALUE can't be used in the same transaction that added it. */
  async function addServiceLine(key: string, label: string) {
    setMutating(true)
    try {
      const { error: addError } = await supabase.rpc('add_service_line', { p_key: key, p_label: label })
      if (addError) throw new Error(addError.message)

      const { error: seedError } = await supabase.rpc('seed_default_milestones', { p_key: key })
      if (seedError) throw new Error(seedError.message)

      await refetch()
    } finally {
      setMutating(false)
    }
  }

  async function setActive(key: string, isActive: boolean) {
    setMutating(true)
    try {
      const { error: updateError } = await supabase
        .from('service_lines')
        .update({ is_active: isActive })
        .eq('key', key)
      if (updateError) throw new Error(updateError.message)
      await refetch()
    } finally {
      setMutating(false)
    }
  }

  return {
    serviceLines,
    activeServiceLines,
    labels,
    getLabel,
    loading,
    error,
    mutating,
    addServiceLine,
    setActive,
    refetch,
  }
}
